import {useEffect, useRef} from 'react';
import type {CampaignCreatorRow} from '../../types/creatorTabTypes';

interface UseCreatorKeyboardNavOptions {
  /** When false, the hook is a no-op — no listeners attached. */
  isActive: boolean;
  /** Full ordered list of creators currently displayed in the table. */
  creators: CampaignCreatorRow[];
  /** ID of the currently previewed creator (or null when none). */
  selectedCreatorId: string | null;
  /** Called when the user presses Up/Down to move to a different creator. */
  onSelectCreator: (creatorId: string) => void;
  /** Called when the user presses Escape. */
  onClose: () => void;
}

/**
 * Enables keyboard navigation across the creators list while the preview
 * side panel is open:
 *   - ArrowUp   → previous creator (no-op at the top of the list)
 *   - ArrowDown → next creator (no-op at the bottom of the list)
 *   - Escape    → close the side panel
 *
 * Keypresses are intentionally ignored when:
 *   - The hook is not active (e.g. side panel closed).
 *   - The user is typing in an input, textarea, select, or contenteditable
 *     element — so the search bar and message composer keep working normally.
 *   - A modifier key (Cmd / Ctrl / Alt / Shift) is held — so browser
 *     shortcuts like Cmd+R or Shift+Tab continue to work.
 */
function useCreatorKeyboardNav({
  isActive,
  creators,
  selectedCreatorId,
  onSelectCreator,
  onClose,
}: UseCreatorKeyboardNavOptions): void {
  // Latest-args ref so the keydown handler always sees fresh state without
  // re-attaching the listener every render (creators reference changes
  // frequently with infinite-scroll loading).
  const optionsRef = useRef({
    creators,
    selectedCreatorId,
    onSelectCreator,
    onClose,
  });
  optionsRef.current = {
    creators,
    selectedCreatorId,
    onSelectCreator,
    onClose,
  };

  useEffect(() => {
    if (!isActive) return undefined;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey || event.shiftKey) {
        return;
      }
      if (isEditableTarget(event.target)) {
        return;
      }

      const {
        creators: currentCreators,
        selectedCreatorId: currentSelectedId,
        onSelectCreator: select,
        onClose: close,
      } = optionsRef.current;

      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }

      if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
        return;
      }

      const currentIndex = currentCreators.findIndex(
        (c) => c.id === currentSelectedId
      );
      if (currentIndex === -1) return;

      const nextIndex =
        event.key === 'ArrowUp' ? currentIndex - 1 : currentIndex + 1;

      if (nextIndex < 0 || nextIndex >= currentCreators.length) return;

      // preventDefault stops the browser from scrolling the page on Up/Down.
      event.preventDefault();
      select(currentCreators[nextIndex].id);
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isActive]);
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tag = target.tagName;
  return (
    tag === 'INPUT' ||
    tag === 'TEXTAREA' ||
    tag === 'SELECT' ||
    target.isContentEditable
  );
}

export default useCreatorKeyboardNav;
